/**
 * The competitive games that record per-player stats and match history. Mirrors
 * the Prisma `Game` enum (uppercased there). Blackjack (بیست‌ویک) is intentionally
 * excluded — it is a solo-vs-dealer mode that does not feed the shared profile
 * stats. Keep this list in sync with the `Game` enum in the Prisma schema.
 */
export const GAMES = [
  "hokm",
  "shelem",
  "pasur",
  "hearts",
  "bluff",
  "chess",
  "backgammon",
  "mensch",
  "dooz",
  // مار پله (Snakes and Ladders) — card-86, 2026-11-03. Prisma `LADDERS`.
  "ladders",
  // هفت خبیث — card-60, 2026-11-05. Prisma `HAFTKHABIS`.
  "haftkhabis",
] as const;
export type GameId = (typeof GAMES)[number];

export function isGameId(value: string): value is GameId {
  return (GAMES as readonly string[]).includes(value);
}

/**
 * Per-game activation state. Mirrors the Prisma `GameStatus` enum on
 * `GameConfig` — the DB is the single source of truth (admin-togglable, live):
 * - `ACTIVE`      — playable by everyone.
 * - `COMING_SOON` — visible in the lobby as «بزودی»; the game-server REJECTS
 *   creating/joining its rooms for everyone except admins (who bypass the gate
 *   to test). Append-only: add future states (e.g. maintenance) at the end.
 */
export const GAME_STATUSES = ["ACTIVE", "COMING_SOON"] as const;
export type GameStatus = (typeof GAME_STATUSES)[number];

export function isGameStatus(value: string): value is GameStatus {
  return (GAME_STATUSES as readonly string[]).includes(value);
}

/**
 * The canonical lobby order — the web renders game tiles exactly in this
 * sequence, and `GET /games` reports each game's `order` as its index here.
 * (Deliberately NOT stored in the DB; reordering is a code-level design call.)
 */
export const LOBBY_GAME_ORDER = [
  "hokm",
  "hearts",
  "shelem",
  "pasur",
  "haftkhabis",
  "bluff",
  "chess",
  // منچ sits ahead of تخته نرد in the board tab (operator's ordering).
  "mensch",
  // دوز and مار پله SWAPPED PLACES (the operator, card-86 round 2, 2026-11-03: «In the lobby's
  // board-games tab, swap the places of «مار پله» and «دوز».»): دوز now sits right after منچ, مار پله
  // at the board tab's END of this list. Nothing else moved.
  // ⚠️ The solo «مار» and the coming Sudoku tile are NOT in this list — they are appended after the
  // grid by `Lobby.tsx` (Sudoku's tile goes last, as promised to that session).
  "dooz",
  "backgammon",
  "ladders",
] as const satisfies readonly GameId[];

/**
 * ⚠️ GAMES HIDDEN FROM PLAYERS — operator, 2026-10-03: «Hide بلوف completely from players: no trace
 * of it anywhere they can see … Keep its code in the repo untouched; we're not deleting it, just
 * not showing it or working on it.»
 *
 * NOT a `GameStatus`: COMING_SOON is the admin's live switch and still SHOWS the tile («بزودی»).
 * This is the code-level decision that nothing a player can reach names the game — admins
 * included, since nobody is working on it. Every player-facing list asks {@link isHiddenGame}: the
 * web's lobby list (the grid, the invite sheet and «برترین‌ها»'s chips all read it), the per-game
 * stats rows, the guide, «ادامه بازی» and a `t_<game>_…` deep link; the bot's tutorials menu, an
 * old menu's button and `/start t_<game>_…`.
 *
 * What deliberately STAYS: the id in `GAMES`, `LOBBY_GAME_ORDER` and every `Record<GameId, …>` map
 * (stored rows must still parse; the admin panel's toggle list is derived from the order), the
 * game's whole code, and the server gate — its row stays COMING_SOON, so a create/join is still
 * refused for everyone but an admin. Un-hiding is deleting the id here.
 */
export const HIDDEN_GAMES: readonly GameId[] = ["bluff"];

/** Whether `game` is hidden from players — `true` for {@link HIDDEN_GAMES}, `false` for anything else. */
export function isHiddenGame(game: string): boolean {
  return (HIDDEN_GAMES as readonly string[]).includes(game);
}

/** Lobby category per game — drives the two lobby tabs «بازی‌های کارتی» / «بازی‌های تخته‌ای».
 *  منچ (Ludo) will join "board" when it ships. */
export type GameCategory = "card" | "board";
export const GAME_CATEGORY: Record<GameId, GameCategory> = {
  hokm: "card",
  hearts: "card",
  shelem: "card",
  pasur: "card",
  bluff: "card",
  chess: "board",
  backgammon: "board",
  // منچ is a dice/board game, so it joins chess + تخته نرد in the board tab.
  mensch: "board",
  // دوز is a two-player board game — the board tab, with شطرنج / منچ / تخته نرد.
  dooz: "board",
  // مار پله — a dice race like منچ, so the board tab.
  ladders: "board",
  haftkhabis: "card",
};
/** Tab order: card games first, board games second. */
export const GAME_CATEGORY_ORDER = ["card", "board"] as const satisfies readonly GameCategory[];

/**
 * Boot-seed defaults: the api upserts a `GameConfig` row per game IF MISSING on
 * every boot (idempotent — an admin's live toggle is never overwritten). Also
 * the fallback when a row is absent (fresh DB) or unreadable (DB hiccup on the
 * game-server gate), so both ends fail to the same launch posture.
 */
export const DEFAULT_GAME_STATUSES: Record<GameId, GameStatus> = {
  hokm: "ACTIVE",
  hearts: "ACTIVE",
  // شلم and پاسور are LIVE from the combined release card-a7 (the operator, 2026-10-21: «In the
  // combined release, open پاسور and شلم for everyone — no «بزودی» on prod»). The PROD and STAGING
  // `GameConfig` rows are flipped by that release's guarded DB step
  // (`infra/activate-pasur-shelem.sh`) — the boot-seed is create-if-missing, so this default
  // governs only the web's pre-load lobby, the gate's DB-error fallback and a fresh DB.
  shelem: "ACTIVE",
  pasur: "ACTIVE",
  bluff: "COMING_SOON",
  // Chess is LIVE (operator flipped it ACTIVE 2026-07-21 after the on-device layout pass). Board-
  // games lobby tab. The prod/staging GameConfig row is flipped in the same deploy (the boot-seed
  // is create-if-missing, so the default here governs only fresh DBs).
  chess: "ACTIVE",
  // تخته نرد is LIVE (operator flipped it 2026-08-16, after the on-device pass and the
  // turn-expiry auto-play fix). It shipped hidden behind «بزودی» for two days — the same posture
  // chess and منچ went through — so admins could play it on prod before anyone else could. The
  // PROD `GameConfig` row is flipped in the same deploy; this default governs only fresh DBs.
  backgammon: "ACTIVE",
  // منچ is LIVE (operator flipped it 2026-07-22). Board-games tab, ahead of تخته نرد. The
  // STAGING GameConfig row is flipped in the same deploy; PROD stays «بزودی» until the
  // operator approves it there (this default governs only fresh DBs).
  mensch: "ACTIVE",
  // دوز shipped COMING_SOON on both environments (operator, 2026-09-03) and is LIVE since
  // 2026-09-04 — its rows say ACTIVE on prod and staging. The default follows its rows from card-a7
  // (the operator, 2026-10-21), so the pre-load lobby no longer flashes «بزودی» on a live game.
  dooz: "ACTIVE",
  // مار پله ships OPEN (the operator, 2026-11-03, via card-86's coordinator: «open right away»), not
  // the «بزودی» posture the other board games shipped in. The boot-seed is create-if-missing, so this
  // default seeds the NEW row ACTIVE on each environment and then governs only the pre-load lobby, the
  // gate's DB-error fallback and a fresh DB; the admin panel's live toggle can still lock it.
  ladders: "ACTIVE",
  // هفت خبیث ships OPEN, as مار پله did (card-60 spec D25); the admin panel's lock remains.
  haftkhabis: "ACTIVE",
};

/**
 * The client-facing error message a Colyseus create/join is rejected with when
 * the game is COMING_SOON and the joining user is not an admin. Mirrors the
 * `MAX_CONCURRENT_GAMES_MESSAGE` pattern: the game-server throws
 * `new Error(GAME_COMING_SOON_MESSAGE)` from `onAuth`, Colyseus surfaces the
 * message verbatim as the join error, and the web matches on the exact string
 * (`error.message === GAME_COMING_SOON_MESSAGE`) to show the «بزودی» treatment.
 */
export const GAME_COMING_SOON_MESSAGE = "بزودی — این بازی هنوز فعال نشده است";

/**
 * The refusal a solo RESUME gets when its save was made under rules this build no longer plays
 * (`GameEngine.canResume` answered false). The game-server deletes that one row, records nothing,
 * and throws `new Error(SAVED_GAME_RETIRED_MESSAGE)` from `onJoin`; Colyseus hands the message to
 * the client verbatim as the join error, and the web matches the exact string
 * (`error.message === SAVED_GAME_RETIRED_MESSAGE`) to show its own notice (`fa.status`).
 *
 * ⚠️ A CONTRACT BETWEEN TWO IMAGES — change the wording only together with the web's match. It is
 * Persian on purpose: at the «ادامه بازی» join, a cached bundle that predates the match shows a
 * Persian refusal verbatim (`localizedReason`), so even the oldest client says something true there.
 * NOT at the reconnect door, where an old bundle retries into «اتصال برقرار نشد»: hence the DEPLOY
 * ORDER on `GameEngine.canResume` (this web first, then any engine that implements the hook).
 */
export const SAVED_GAME_RETIRED_MESSAGE = "این بازی ذخیره‌شده دیگر قابل ادامه نیست";

/**
 * How sharply the AI bots play. Chosen per-user/per-table (the client passes it in
 * a room's create/join options); the game-server maps it to each game's bot factory
 * (`@bing/ai`). Shared vocabulary so the web selector, the persisted preference and
 * the server-side validation all agree on the exact three levels.
 */
export const BOT_DIFFICULTIES = ["easy", "medium", "hard"] as const;
export type BotDifficulty = (typeof BOT_DIFFICULTIES)[number];

/** The server-wide fallback when a client sends no (or an invalid) difficulty. */
export const DEFAULT_BOT_DIFFICULTY: BotDifficulty = "medium";

export function isBotDifficulty(value: unknown): value is BotDifficulty {
  return typeof value === "string" && (BOT_DIFFICULTIES as readonly string[]).includes(value);
}

/**
 * منچ HAS TWO LEVELS, NOT THREE — and that is a property of the game, not a shortcut.
 *
 * ⚠️ IT SHIPPED THREE FOR A WHILE, AND TWO OF THEM WERE THE SAME OPPONENT. `medium` and `hard`
 * ran the identical scoring function, so a head-to-head measured EXACTLY 50.0% with a zero
 * margin. The cause is structural: MEASURED over 11,078 plies of 2-player منچ, **80.3% of turns
 * offer no choice at all** (you roll, or exactly one token can legally move). One die and at most
 * four candidates is simply not enough room for three distinguishable opponents — a third label
 * would be a promise the game cannot keep.
 *
 * So the player picks between TWO:
 *   normal — takes the obvious good move (finish > capture > home row > release > progress)
 *   hard   — values the POSITION each move leads to, and prices the reply it invites
 * Measured head-to-head on fresh seeds: hard beats normal 61.4% (n=500).
 */
export const MENSCH_BOT_LEVELS = ["normal", "hard"] as const;
export type MenschBotLevel = (typeof MENSCH_BOT_LEVELS)[number];

/**
 * منچ DEFAULTS TO ITS PROFESSIONAL BOT («حریف حرفه‌ای»), not the normal one.
 *
 * It can afford to: MEASURED over 5,537 real منچ decision points, `hard` costs **1.83 µs** per
 * decision against `normal`'s 0.28 µs. That is 6.5× of almost nothing — at 200 concurrent
 * all-bot tables it is 0.017% of ONE core. (For scale, حکم's hard tier is ~10 ms a decision,
 * five thousand times more.) So the stronger opponent is what everyone gets unless they choose
 * otherwise, rather than a reward for finding the picker.
 */
export const DEFAULT_MENSCH_BOT_LEVEL: MenschBotLevel = "hard";

/** The wire value that means {@link DEFAULT_MENSCH_BOT_LEVEL} — what a client sends by default. */
export const DEFAULT_MENSCH_DIFFICULTY: BotDifficulty = "hard";

export function isMenschBotLevel(value: unknown): value is MenschBotLevel {
  return typeof value === "string" && (MENSCH_BOT_LEVELS as readonly string[]).includes(value);
}

/**
 * The three-valued wire vocabulary → منچ's two levels.
 *
 * The room's create/join options carry a {@link BotDifficulty} for EVERY game, so منچ maps rather
 * than inventing a second option shape. `easy` folds into `normal`: a منچ bot that moves at random
 * is not a difficulty setting, it is a broken opponent — and this way an old or crafted client
 * sending `easy` gets a sane bot instead of a third tier that no longer exists.
 */
export function menschLevelFrom(difficulty: BotDifficulty): MenschBotLevel {
  return difficulty === "hard" ? "hard" : "normal";
}

/** …and back, for the client control (which speaks the shared vocabulary). */
export const MENSCH_DIFFICULTY_CHOICES: readonly BotDifficulty[] = ["medium", "hard"];

/**
 * Outcome of a recorded match from a single player's perspective. Mirrors the
 * Prisma `MatchResult` enum. `draw` covers ties (e.g. a tied team score) so no
 * migration is needed when the game-server starts writing history.
 */
export const MATCH_RESULTS = ["win", "loss", "draw"] as const;
export type MatchResult = (typeof MATCH_RESULTS)[number];
